import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Room, DocumentSet, Task, Note, Trade, PageMapping, Document, Attachment, getRoom, getRoomBySlug, getDocumentSets, getRoomTasks, getRoomNotes, getTrades, getPageMappings, getPageImageUrl, getRoomsBySlug, getProjectSlug, getDocumentPagesMetadata, getRoomAttachments, getAttachmentDownloadUrl, getAttachmentThumbnailUrl, getRoomSchedules, RoomScheduleData } from '../api/client';
import { DocumentThumbnail } from '../components/DocumentThumbnail';
import { TaskList } from '../components/TaskList';
import { NotesList } from '../components/NotesList';
import { DocumentImagePortal } from '../components/DocumentImagePortal';

interface DocumentSection {
  document: Document;
  mappings: PageMapping[];
}

const MAX_AUTOCOMPLETE_RESULTS = 10;

export function RoomView() {
  const { projectId, roomId, projectSlug, roomNumber } = useParams<{
    projectId?: string;
    roomId?: string;
    projectSlug?: string;
    roomNumber?: string;
  }>();
  const navigate = useNavigate();

  const [room, setRoom] = useState<Room | null>(null);
  const [documentSets, setDocumentSets] = useState<DocumentSet[]>([]);
  const [pageMappings, setPageMappings] = useState<PageMapping[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOldPlans, setShowOldPlans] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [schedules, setSchedules] = useState<RoomScheduleData[]>([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState(0);

  // Document image portal state
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalDocumentId, setPortalDocumentId] = useState<number | null>(null);
  const [portalPages, setPortalPages] = useState<number[]>([]);
  const [portalInitialPage, setPortalInitialPage] = useState(1);
  const [portalDocumentName, setPortalDocumentName] = useState('');

  // Room navigation state
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [currentProjectSlug, setCurrentProjectSlug] = useState<string | null>(null);
  const [roomNumberInput, setRoomNumberInput] = useState('');
  const [roomNameInput, setRoomNameInput] = useState('');
  const [showDropUp, setShowDropUp] = useState(false);
  const [navError, setNavError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [nameManuallySet, setNameManuallySet] = useState(false);
  const dropUpRef = useRef<HTMLDivElement>(null);

  // Determine which URL format is being used
  const isSlugBased = !!(projectSlug && roomNumber);
  const isIdBased = !!(projectId && roomId);

  // Open document in portal or fallback to PDF
  const handleOpenDocumentPortal = async (document: Document, initialPage: number, mappings?: PageMapping[]) => {
    if (!room) return;
    
    try {
      const metadata = await getDocumentPagesMetadata(document.id);
      
      if (metadata.conversion_status === 'ready' && metadata.pages.length > 0) {
        // Use WebP pages
        let pagesToShow: number[];
        
        if (mappings && mappings.length > 0) {
          // Show only mapped pages, sorted
          pagesToShow = mappings
            .map(m => m.page_number)
            .filter((value, index, self) => self.indexOf(value) === index)
            .sort((a, b) => a - b);
        } else {
          // Show all pages
          pagesToShow = metadata.pages;
        }
        
        setPortalDocumentId(document.id);
        setPortalPages(pagesToShow);
        setPortalInitialPage(initialPage);
        setPortalDocumentName(document.filename.replace(/\.[^/.]+$/, ''));
        setPortalOpen(true);
      } else {
        // Fallback to PDF viewer
        navigate(`/view/${room.project_id}/${room.id}/doc/${document.id}?page=${initialPage}`);
      }
    } catch (error) {
      console.error('Failed to load document metadata:', error);
      // Fallback to PDF viewer
      navigate(`/view/${room.project_id}/${room.id}/doc/${document.id}?page=${initialPage}`);
    }
  };

  const loadData = useCallback(async () => {
    if (!isSlugBased && !isIdBased) return;
    try {
      // First, get the room data using the appropriate method
      let roomData: Room;
      if (isSlugBased) {
        roomData = await getRoomBySlug(projectSlug!, roomNumber!);
      } else {
        roomData = await getRoom(Number(projectId), Number(roomId));
      }

      // Set room immediately so we show something even if related data fails
      setRoom(roomData);

      // Get project slug for navigation
      let slug: string;
      if (isSlugBased) {
        slug = projectSlug!;
      } else {
        slug = await getProjectSlug(roomData.project_id);
      }
      setCurrentProjectSlug(slug);

      // Load related information with graceful error handling for each
      const results = await Promise.allSettled([
        getDocumentSets(roomData.project_id),
        getPageMappings(roomData.id),
        getRoomTasks(roomData.id),
        getRoomNotes(roomData.id),
        getTrades(),
        getRoomsBySlug(slug),
        getRoomAttachments(roomData.id),
      ]);

      // Extract successful results, use empty arrays for failures
      const docSets = results[0].status === 'fulfilled' ? results[0].value : [];
      const mappings = results[1].status === 'fulfilled' ? results[1].value : [];
      const taskData = results[2].status === 'fulfilled' ? results[2].value : [];
      const noteData = results[3].status === 'fulfilled' ? results[3].value : [];
      const tradeData = results[4].status === 'fulfilled' ? results[4].value : [];
      const roomsData = results[5].status === 'fulfilled' ? results[5].value : [];
      const attachmentData = results[6].status === 'fulfilled' ? results[6].value : [];

      // Load schedules separately with error handling (not critical for room display)
      let scheduleData: RoomScheduleData[] = [];
      try {
        scheduleData = await getRoomSchedules(roomData.id);
      } catch (err) {
        console.error('Failed to load schedules:', err);
        // Schedule errors shouldn't break the room view
      }

      setDocumentSets(docSets);
      setPageMappings(mappings);
      setTasks(taskData);
      setNotes(noteData);
      setTrades(tradeData);
      setAllRooms(roomsData);
      setAttachments(attachmentData);
      setSchedules(scheduleData);
    } catch (error) {
      console.error('Failed to load room data:', error);
    } finally {
      setLoading(false);
    }
  }, [isSlugBased, isIdBased, projectSlug, roomNumber, projectId, roomId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!room) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [room?.id]);

  // After thumbnails are displayed, preload full-size images for mapped pages
  useEffect(() => {
    if (pageMappings.length === 0) return;

    // Only preload current (not archived) mapped pages
    const currentMappings = pageMappings.filter(m => m.document?.is_current !== false);

    currentMappings.forEach(mapping => {
      if (mapping.document?.conversion_status === 'ready') {
        const img = new Image();
        img.src = getPageImageUrl(mapping.document.id, mapping.page_number);
      }
    });
  }, [pageMappings]);

  // Filter rooms for autocomplete based on name input
  const filteredRooms = roomNameInput.length > 0
    ? allRooms.filter(r => {
        const searchLower = roomNameInput.toLowerCase();
        const numberMatch = r.number.toLowerCase().includes(searchLower);
        const nameMatch = r.name?.toLowerCase().includes(searchLower);
        return numberMatch || nameMatch;
      }).slice(0, MAX_AUTOCOMPLETE_RESULTS)
    : [];

  // Handle room navigation
  const handleRoomNavigate = useCallback(() => {
    const targetNumber = roomNumberInput.trim();

    if (!currentProjectSlug) {
      setNavError('Unable to navigate - project information not loaded');
      return;
    }

    let resolvedRoomNumber = targetNumber;

    if (!resolvedRoomNumber && roomNameInput.trim().length > 0) {
      if (allRooms.length > 0) {
        const searchLower = roomNameInput.toLowerCase().trim();
        const exactMatch = allRooms.find(r =>
          r.number.toLowerCase() === searchLower || r.name?.toLowerCase() === searchLower
        );
        if (exactMatch) {
          resolvedRoomNumber = exactMatch.number;
        } else if (filteredRooms.length > 0) {
          resolvedRoomNumber = filteredRooms[0].number;
        }
      }
    }

    if (!resolvedRoomNumber) {
      setNavError('Please enter a room number or name');
      return;
    }

    // If rooms haven't loaded yet, try navigating anyway
    if (allRooms.length === 0) {
      navigate(`/${currentProjectSlug}/${resolvedRoomNumber}`);
      return;
    }

    // Check if room exists
    const targetRoom = allRooms.find(r => r.number === resolvedRoomNumber);
    if (!targetRoom) {
      setNavError("Room doesn't exist");
      setRoomNumberInput('');
      setRoomNameInput('');
      return;
    }

    setRoomNumberInput(targetRoom.number);
    setRoomNameInput('');
    setShowDropUp(false);
    setHighlightedIndex(-1);

    // Navigate to the room using slug-based URL
    navigate(`/${currentProjectSlug}/${targetRoom.number}`);
  }, [roomNumberInput, roomNameInput, currentProjectSlug, allRooms, filteredRooms, navigate]);

  // Handle selecting a room from the dropdown
  const handleSelectRoom = useCallback((room: Room) => {
    if (!currentProjectSlug) {
      setNavError('Unable to navigate - project information not loaded');
      return;
    }
    setRoomNumberInput(room.number);
    setRoomNameInput('');
    setShowDropUp(false);
    setHighlightedIndex(-1);
    setNavError(null);
    navigate(`/${currentProjectSlug}/${room.number}`);
  }, [currentProjectSlug, navigate]);

  // Handle keyboard navigation in dropdown
  const handleNameInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropUp || filteredRooms.length === 0) {
      if (e.key === 'Enter') {
        handleRoomNavigate();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredRooms.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredRooms.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredRooms.length) {
          handleSelectRoom(filteredRooms[highlightedIndex]);
        } else if (filteredRooms.length > 0) {
          handleSelectRoom(filteredRooms[0]);
        } else {
          handleRoomNavigate();
        }
        break;
      case 'Escape':
        setShowDropUp(false);
        setHighlightedIndex(-1);
        break;
    }
  }, [showDropUp, filteredRooms, highlightedIndex, handleSelectRoom, handleRoomNavigate]);

  // Reset highlighted index when filtered rooms change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [roomNameInput]);

  // Auto-populate room name when room number changes
  useEffect(() => {
    // Don't override if user manually edited the name field
    if (nameManuallySet) return;

    const trimmedNumber = roomNumberInput.trim();
    if (trimmedNumber && allRooms.length > 0) {
      const matchedRoom = allRooms.find(r => r.number === trimmedNumber);
      if (matchedRoom) {
        // Auto-populate name field with room display name
        const displayName = matchedRoom.name
          ? `${matchedRoom.number} - ${matchedRoom.name}`
          : matchedRoom.number;
        setRoomNameInput(displayName);
      } else {
        // No match found, clear the name field
        setRoomNameInput('');
      }
    } else if (!trimmedNumber) {
      // Clear name input when room number is cleared
      setRoomNameInput('');
    }
  }, [roomNumberInput, allRooms, nameManuallySet]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!room) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Room not found</div>;
  }

  // Filter mappings by document is_current status
  const currentMappings = pageMappings.filter(m => m.document?.is_current !== false);
  const archivedMappings = pageMappings.filter(m => m.document?.is_current === false);

  // Group current page mappings by document
  const documentSections: DocumentSection[] = [];
  const documentGroups = new Map<number, DocumentSection>();

  currentMappings.forEach(mapping => {
    if (!mapping.document) return;
    const docId = mapping.document.id;
    const existing = documentGroups.get(docId);
    if (existing) {
      existing.mappings.push(mapping);
    } else {
      documentGroups.set(docId, { document: mapping.document, mappings: [mapping] });
    }
  });

  // Convert to array and sort by document filename
  documentGroups.forEach((section) => {
    documentSections.push(section);
  });
  documentSections.sort((a, b) => a.document.filename.localeCompare(b.document.filename));

  // Group archived page mappings by document
  const archivedDocumentSections: DocumentSection[] = [];
  const archivedDocumentGroups = new Map<number, DocumentSection>();

  archivedMappings.forEach(mapping => {
    if (!mapping.document) return;
    const docId = mapping.document.id;
    const existing = archivedDocumentGroups.get(docId);
    if (existing) {
      existing.mappings.push(mapping);
    } else {
      archivedDocumentGroups.set(docId, { document: mapping.document, mappings: [mapping] });
    }
  });

  // Convert to array and sort by document filename
  archivedDocumentGroups.forEach((section) => {
    archivedDocumentSections.push(section);
  });
  archivedDocumentSections.sort((a, b) => a.document.filename.localeCompare(b.document.filename));

  return (
    <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto', fontFamily: 'system-ui' }}>
      <h1 style={{ marginBottom: '8px' }}>
        Room {room.number} {room.name && `- ${room.name}`}
      </h1>

      {/* Document sections with PDF thumbnails */}
      {documentSections.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>This Room's Pages</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {documentSections.map(section => {
              // Get first mapping for thumbnail
              const firstMapping = section.mappings[0];
              // Remove file extension for display name
              const displayName = section.document.filename.replace(/\.[^/.]+$/, '');

              return (
                <DocumentThumbnail
                  key={section.document.id}
                  name={displayName}
                  pageCount={section.mappings.length}
                  documentId={section.document.id}
                  pageNumber={firstMapping?.page_number || 1}
                  onClick={() => {
                    handleOpenDocumentPortal(section.document, firstMapping.page_number, section.mappings);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Old Plans (Archived) - Collapsible Section */}
      {archivedMappings.length > 0 && (
        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
          <button
            onClick={() => setShowOldPlans(!showOldPlans)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 0'
            }}
          >
            <span style={{ fontFamily: 'monospace' }}>{showOldPlans ? '\u25BC' : '\u25B6'}</span>
            Old Plans ({archivedMappings.length})
          </button>
          {showOldPlans && (
            <div style={{
              marginTop: '12px',
              opacity: 0.7
            }}>
              {archivedDocumentSections.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {archivedDocumentSections.map(section => {
                    const firstMapping = section.mappings[0];
                    const displayName = section.document.filename.replace(/\.[^/.]+$/, '');

                    return (
                      <DocumentThumbnail
                        key={section.document.id}
                        name={displayName}
                        pageCount={section.mappings.length}
                        documentId={section.document.id}
                        pageNumber={firstMapping?.page_number || 1}
                        onClick={() => {
                          handleOpenDocumentPortal(section.document, firstMapping.page_number, section.mappings);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>Attachments</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {attachments.map(att => {
              const isImage = /^image\//i.test(att.file_type) || /\.(jpg|jpeg|png|webp|gif)$/i.test(att.filename);
              const downloadUrl = getAttachmentDownloadUrl(att.id);

              return (
                <div
                  key={att.id}
                  style={{
                    width: '120px',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                  onClick={() => {
                    if (isImage) {
                      window.open(downloadUrl, '_blank');
                    } else {
                      const a = document.createElement('a');
                      a.href = downloadUrl;
                      a.download = att.display_name || att.filename;
                      a.click();
                    }
                  }}
                >
                  {isImage ? (
                    <div style={{
                      width: '120px',
                      height: '90px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      border: '1px solid #ddd',
                      background: '#f5f5f5',
                    }}>
                      <img
                        src={getAttachmentThumbnailUrl(att.id)}
                        alt={att.display_name || att.filename}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          // Fallback to full image if thumbnail fails
                          (e.target as HTMLImageElement).src = downloadUrl;
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      width: '120px',
                      height: '90px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      background: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '36px',
                    }}>
                      {/\.pdf$/i.test(att.filename) ? '\uD83D\uDCC4' : /\.(xls|xlsx|csv)$/i.test(att.filename) ? '\uD83D\uDCCA' : '\uD83D\uDCC1'}
                    </div>
                  )}
                  <div style={{
                    marginTop: '4px',
                    fontSize: '12px',
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {att.display_name || att.filename}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Complete Documents */}
      {documentSets.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>Full Documents</h2>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
            Browse complete document sets
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {documentSets.map(ds => {
              // Find a document in this set from mappings for thumbnail
              const mappingWithDoc = pageMappings.find(m => m.document?.document_set_id === ds.id);
              const thumbnailDocumentId = mappingWithDoc?.document?.id ?? ds.latest_document_id ?? undefined;

              return (
                <DocumentThumbnail
                  key={ds.id}
                  name={ds.name}
                  pageCount={0}
                  documentId={thumbnailDocumentId}
                  pageNumber={1}
                  onClick={() => {
                    // For full document sets, we need to find the actual document
                    const doc = mappingWithDoc?.document;
                    if (doc) {
                      handleOpenDocumentPortal(doc, 1);
                    } else if (ds.latest_document_id) {
                      // Fetch document info or fallback to PDF
                      navigate(`/view/${room.project_id}/${room.id}/docs/${ds.id}?full=true`);
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Schedules */}
      {schedules.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setScheduleOpen(!scheduleOpen)}
            style={{
              background: 'none',
              border: 'none',
              padding: '0',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 600,
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: scheduleOpen ? '12px' : '0',
            }}
          >
            <span style={{ fontSize: '14px' }}>{scheduleOpen ? '▼' : '▶'}</span>
            Schedules
          </button>

          {scheduleOpen && (
            <>
              {/* Tab bar */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {schedules.map((schedule, idx) => {
                  const label = (schedule.custom_name || schedule.schedule_type);
                  const displayLabel = label.charAt(0).toUpperCase() + label.slice(1);
                  const isActive = activeSchedule === idx;
                  return (
                    <button
                      key={schedule.schedule_id}
                      onClick={() => setActiveSchedule(idx)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '16px',
                        border: isActive ? '1px solid #007bff' : '1px solid #ddd',
                        background: isActive ? '#007bff' : '#fff',
                        color: isActive ? '#fff' : '#333',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: isActive ? 500 : 400,
                      }}
                    >
                      {displayLabel}
                    </button>
                  );
                })}
              </div>

              {/* Active schedule table */}
              {schedules[activeSchedule] && (() => {
                const active = schedules[activeSchedule];
                const dateStr = active.schedule_date ? (() => {
                  const d = new Date(active.schedule_date + 'T00:00:00');
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  const yy = String(d.getFullYear()).slice(-2);
                  return `${mm}/${dd}/${yy}`;
                })() : '';
                const displayName = active.filename || '';

                return (
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '16px',
                    background: '#fff',
                  }}
                >
                  {(dateStr || displayName) && (
                    <div style={{
                      fontSize: '13px',
                      color: '#555',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '1px solid #eee',
                    }}>
                      {dateStr && <span>Date {dateStr}</span>}
                      {dateStr && displayName && <span>  -  </span>}
                      {displayName && <span>{displayName}</span>}
                    </div>
                  )}
                  {active.rows.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '13px',
                        }}
                      >
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            {(schedules[activeSchedule].columns.length > 0
                              ? schedules[activeSchedule].columns
                              : Object.keys(schedules[activeSchedule].rows[0]).filter(k => k !== '_row_index')
                            ).map((key) => (
                              <th
                                key={key}
                                style={{
                                  padding: '8px',
                                  textAlign: 'left',
                                  fontWeight: 'bold',
                                  borderBottom: '1px solid #ddd',
                                  fontSize: '12px',
                                }}
                              >
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {schedules[activeSchedule].rows.map((row, idx) => {
                            const cols = schedules[activeSchedule].columns.length > 0
                              ? schedules[activeSchedule].columns
                              : Object.keys(row).filter(k => k !== '_row_index');
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                {cols.map((key) => (
                                  <td
                                    key={key}
                                    style={{
                                      padding: '8px',
                                      fontSize: '13px',
                                    }}
                                  >
                                    {String(row[key] ?? '')}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                      No data available for this room.
                    </p>
                  )}
                </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      <TaskList
        roomId={room.id}
        tasks={tasks}
        trades={trades}
        onTasksChange={loadData}
      />

      <NotesList
        roomId={room.id}
        notes={notes}
        onNotesChange={loadData}
      />

      {/* Room Navigation */}
      {currentProjectSlug && (
        <div
          style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #eee' }}
          role="search"
          aria-label="Navigate to a different room"
        >
          <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#666' }}>Go to a Different Room</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Room #"
              aria-label="Room number"
              value={roomNumberInput}
              onChange={(e) => {
                setRoomNumberInput(e.target.value);
                setNameManuallySet(false); // Allow auto-population again
                setNavError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRoomNavigate();
                }
              }}
              style={{
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '80px',
                flexShrink: 0
              }}
            />
            <div style={{ position: 'relative', flex: 1, minWidth: '150px' }}>
              <input
                type="text"
                placeholder="or search by name..."
                aria-label="Search rooms by name"
                aria-expanded={showDropUp && filteredRooms.length > 0}
                aria-controls="room-search-listbox"
                aria-activedescendant={highlightedIndex >= 0 ? `room-option-${highlightedIndex}` : undefined}
                role="combobox"
                aria-autocomplete="list"
                value={roomNameInput}
                onChange={(e) => {
                  setRoomNameInput(e.target.value);
                  setNameManuallySet(true); // User manually typed
                  setShowDropUp(e.target.value.length > 0);
                  setNavError(null);
                }}
                onFocus={() => {
                  if (roomNameInput.length > 0) setShowDropUp(true);
                }}
                onBlur={(e) => {
                  // Check if focus is moving to the dropdown
                  if (dropUpRef.current?.contains(e.relatedTarget as Node)) {
                    return;
                  }
                  setShowDropUp(false);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleNameInputKeyDown}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
              {/* Drop-up menu */}
              {showDropUp && filteredRooms.length > 0 && (
                <div
                  ref={dropUpRef}
                  id="room-search-listbox"
                  role="listbox"
                  aria-label="Room search results"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                    zIndex: 10
                  }}
                >
                  {filteredRooms.map((r, index) => (
                    <div
                      key={r.id}
                      id={`room-option-${index}`}
                      role="option"
                      aria-selected={index === highlightedIndex}
                      onMouseDown={() => handleSelectRoom(r)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '14px',
                        background: index === highlightedIndex ? '#f5f5f5' : 'white'
                      }}
                    >
                      {r.number}{r.name ? ` - ${r.name}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleRoomNavigate}
              aria-label="Go to room"
              style={{
                padding: '10px 20px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              Go
            </button>
          </div>
          {navError && (
            <div
              role="alert"
              aria-live="polite"
              style={{ color: '#dc3545', fontSize: '14px', marginTop: '8px' }}
            >
              {navError}
            </div>
          )}
          {!navError && roomNameInput.trim().length > 0 && filteredRooms.length === 0 && (
            <div style={{ color: '#666', fontSize: '13px', marginTop: '8px' }}>
              No rooms match that search.
            </div>
          )}
        </div>
      )}

      {/* Document Image Portal */}
      {portalOpen && portalDocumentId && room && (
        <DocumentImagePortal
          documentId={portalDocumentId}
          allowedPages={portalPages}
          initialPage={portalInitialPage}
          documentName={portalDocumentName}
          onClose={() => setPortalOpen(false)}
          onPdfFallback={() => {
            setPortalOpen(false);
            navigate(`/view/${room.project_id}/${room.id}/doc/${portalDocumentId}?page=${portalInitialPage}`);
          }}
        />
      )}
    </div>
  );
}
